import { useEffect, useRef, useState } from 'react';
import {
  Centrifuge,
  type Options as CentrifugeOptions,
  type Subscription
} from 'centrifuge';
import { webSocketUrl } from '@/app/common/api/config';

const CHANNEL_PREFIX = 'personal:#';

export interface CrawlProgressData {
  state?: string;
  url_crawled?: number;
  url_remaining?: number;
  [key: string]: unknown;
}

export interface UseCentrifugeProgressOptions {
  jobPage?: string | null;
  token?: string | null;
  isSubmitted?: boolean;
  onDone?: () => void;
  onCanceled?: () => void;
}

export const useCentrifugeProgress = ({
  jobPage,
  token,
  onDone,
  onCanceled
}: UseCentrifugeProgressOptions) => {
  const centrifugeRef = useRef<Centrifuge | null>(null);
  const subscriptionRef = useRef<Subscription | null>(null);
  const [progressData, setProgressData] = useState<CrawlProgressData | null>(
    null
  );
  const [connected, setConnected] = useState(false);

  useEffect(() => {
    if (!jobPage || !token || centrifugeRef.current) return;

    const centrifuge = new Centrifuge(
      webSocketUrl,
      {
        getToken: async () => token,
        ping: 10000,
        maxReconnect: 10,
        timeout: 30000
      } as unknown as Partial<CentrifugeOptions>
    );

    centrifugeRef.current = centrifuge;

    centrifuge.on('connected', () => {
      console.log(`Centrifuge connected for job ${jobPage}`);
      setConnected(true);
    });

    centrifuge.on('disconnected', () => {
      console.log(`Centrifuge disconnected for job ${jobPage}`);
      setConnected(false);
    });

    centrifuge.on('error', errorContext => {
      console.error('Centrifuge error:', errorContext);
    });

    const sub = centrifuge.newSubscription(CHANNEL_PREFIX + jobPage);

    sub.on('publication', context => {
      try {
        const data =
          typeof context.data === 'string'
            ? (JSON.parse(context.data) as CrawlProgressData)
            : (context.data as CrawlProgressData);
        setProgressData(data);

        if (data.state === 'entertheclose') {
          onCanceled?.();
        }

        if (['Done', 'پایان'].includes(data.state ?? '')) {
          onDone?.();
        }
      } catch (error) {
        console.error('Error parsing progress data:', error);
      }
    });

    sub.subscribe();
    subscriptionRef.current = sub;

    centrifuge.connect();

    return () => {
      subscriptionRef.current?.unsubscribe();
      centrifugeRef.current?.disconnect();
      centrifugeRef.current = null;
      subscriptionRef.current = null;
    };
  }, [jobPage, token]);

  return { progressData, connected };
};
